import React, { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Users } from './api/Users';
import './App.scss';
import AppNavbar from './components/AppNavbar';
import AppSidebar from './components/AppSidebar';
import { AuthProvider, useAuth } from './contexts/auth';
import { FullScreenLoaderProvider } from './contexts/fullScreenLoader';
import { ToastsProvider, useToasts } from './contexts/toasts';
import Account from './views/Account';
import ForgotPassword from './views/ForgotPassword';
import GroupMembers from './views/groups/GroupMembers';
import GroupsList from './views/groups/GroupsList';
import Home from './views/Home';
import Login from './views/Login';
import OrganizationEdit from './views/organizations/OrganizationEdit';
import OrganizationsList from './views/organizations/OrganizationsList';
import PathologiesList from './views/pathologies/PathologiesList';
import ProtocolSessions from './views/protocols/ProtocolSessions';
import ProtocolsList from './views/protocols/ProtocolsList';
import TasksList from './views/tasks/TasksList';
import UserEdit from './views/users/UserEdit';
import UsersList from './views/users/UsersList';
import TaskEdit from './views/tasks/TaskEdit';
import TenantProtocolList from './views/protocols/TenantProtocolList';
import ProtocolDetail from './views/protocols/ProtocolDetail';
import BatteriesList from './views/batteries/BatteriesList';
import BatteryDetail from './views/batteries/BatteryDetail';
import BatteryTasks from './views/batteries/BatteryTasks';
import TenantBatteriesList from './views/batteries/TenantBatteriesList';
import BatteryTenantDetail from './views/batteries/BatteryTenantDetail';
import SchedulerPage from './views/scheduler/SchedulerPage';
import DeviceDetail from './views/devices/DeviceDetail';
import DevicesList from './views/devices/DevicesList';
import TenantDevicesList from './views/devices/TenantDevicesList';
import DeviceTenantDetail from './views/devices/DeviceTenantDetail';
import DoctorsList from './views/doctors/DoctorsList';
import PatientsList from './views/patients/PatientsList';
import PatientEdit from './views/patients/PatientEdit';
import DoctorEdit from './views/doctors/DoctorEdit';
import ReadOnlyCalendar from './views/scheduler/ReadOnlyCalendar';
import UserDetail from './views/users/UserDetail';
import DoctorDetail from './views/doctors/DoctorDetail';
import PatientDetail from './views/patients/PatientDetail';
import TenantOrganizationEdit from './views/organizations/TenantOrganizationEdit';
import PatientPrivacyEdit from './views/patients/PatientPrivacyEdit';
import MetricsList from './views/metrics/MetricsList';
import TherapyDetail from './views/therapy/TherapyDetail';
import TelemetryUpload from './views/telemetry/TelemetryUpload';


const AppPagesComponent = () => {
  const { loggedUser } = useAuth();
  const [show, setShow] = useState(false);

  const handleClose = () => setShow(false);
  const handleOpen = () => setShow(true);

  return (
    <div className="layout-wrapper">
      <AppNavbar showSidebar={handleOpen} />
      <AppSidebar show={show} onClose={handleClose} />
      <div className="page-content">
        <Routes>
          <Route index element={<Navigate to="/home" />} />
          <Route path="/home" element={<Home />} />
          <Route path="/users">
            <Route index element={<UsersList />} />
            <Route path="register" element={<UserEdit />} />
            <Route path=":id/edit" element={<UserEdit />} />
            <Route path=":id/calendar" element={<SchedulerPage />} />
            <Route path=":id/details" element={<UserDetail />} />
          </Route>
          <Route path="/doctors">
            <Route index element={<DoctorsList />} />
            <Route path="register" element={<DoctorEdit />} />
            <Route path=":id/edit" element={<DoctorEdit />} />
          </Route>
          <Route path="/patients">
            <Route index element={<PatientsList />} />
            <Route path="register" element={loggedUser?.privacyMode ? <PatientPrivacyEdit /> : <PatientEdit />} />
            <Route path=":id/edit" element={loggedUser?.privacyMode ? <PatientPrivacyEdit /> : <PatientEdit />} />
            <Route path=":id/calendar" element={<SchedulerPage />} />
          </Route>
          <Route path="/me">
            <Route path="calendar" element={<ReadOnlyCalendar />} />
          </Route>
          <Route path="/groups">
            <Route index element={<GroupsList />} />
            <Route path=":id/manage-members" element={<GroupMembers />} />
          </Route>
          <Route path="/pathologies">
            <Route index element={<PathologiesList />} />
          </Route>
          <Route path="/tasks">
            <Route index element={<TasksList />} />
            <Route path="create" element={<TaskEdit />} />
            <Route path=":id/edit" element={<TaskEdit />} />
          </Route>
          <Route path="/protocols">
            <Route index element={<ProtocolsList />} />
            <Route path=":id/manage-sessions" element={<ProtocolSessions />} />
            <Route path=":id/details" element={<ProtocolDetail />} />
          </Route>
          <Route path="/tenant-protocols">
            <Route index element={<TenantProtocolList />} />
          </Route>
          <Route path="/therapy">
            <Route index element={<Navigate to="/home" />} />
            <Route path=":id" element={<TherapyDetail />} />
          </Route>
          <Route path="/batteries">
            <Route index element={<BatteriesList />} />
            <Route path=":id/manage-tasks" element={<BatteryTasks />} />
            <Route path=":id/details" element={<BatteryDetail />} />
          </Route>
          <Route path="/devices">
            <Route index element={<DevicesList />} />
            <Route path=":id/details" element={<DeviceDetail />} />
          </Route>
          <Route path="/tenant-batteries">
            <Route index element={<TenantBatteriesList />} />
            <Route path=":id/details" element={<BatteryTenantDetail />} />
          </Route>
          <Route path="/tenant-devices">
            <Route index element={<TenantDevicesList />} />
            <Route path=":id/details" element={<DeviceTenantDetail />} />
          </Route>
          <Route path="/organizations">
            <Route index element={<OrganizationsList />} />
            <Route path="create" element={<OrganizationEdit />} />
            <Route path=":id/edit" element={<OrganizationEdit />} />
          </Route>
          <Route path="/metrics">
            <Route index element={<MetricsList />} />
          </Route>
          <Route path="/telemetry" element={<TelemetryUpload />} />
          <Route path="/organization" element={<TenantOrganizationEdit />} />
          <Route path="/account" element={<Account />} />
          <Route path="*" element={<Navigate to="/home" />} />
        </Routes>
      </div>
    </div>
  );
}

function App() {
  const { token, setLoggedUser } = useAuth();
  const { showToast } = useToasts();

  useEffect(() => {
    if (token) {
      Users.getCurrentUser()
        .then(
          ({ data }) => {
            setLoggedUser!(data);
          },
          (err) => {
            const message = err.response?.data?.message || err.message;
            const status = err.response?.status;
            showToast!('user-error', message, `User Error ${status ? `(${status})` : ''}`, 'danger');
          }
        )
    }
    // eslint-disable-next-line
  }, [token]);

  return (
    <main className="app-root">
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route>
          {token && <Route path="*" element={<AppPagesComponent />} />}
          {!token && <Route path="*" element={<Navigate to="/login" />} />}
        </Route>
      </Routes>
    </main>
  );
}

export default function withProvider() {
  return (
    <FullScreenLoaderProvider>
      <ToastsProvider>
        <AuthProvider>
          <App />
        </AuthProvider>
      </ToastsProvider>
    </FullScreenLoaderProvider>
  )
};

